// =============================================================================
// MeuDriver — Batch P3: Hooks de Anúncios da Plataforma
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AnnouncementAudience = 'all' | 'students' | 'instructors'
export type AnnouncementSeverity = 'info' | 'warning' | 'critical'

export interface Announcement {
  id: string
  title: string
  body: string
  target_audience: AnnouncementAudience
  severity: AnnouncementSeverity
  is_active: boolean
  starts_at: string
  ends_at: string | null
  created_by: string | null
  created_at: string
}

export interface CreateAnnouncementInput {
  title: string
  body: string
  target_audience?: AnnouncementAudience
  severity?: AnnouncementSeverity
  is_active?: boolean
  starts_at?: string
  ends_at?: string | null
}

export interface UpdateAnnouncementInput extends Partial<CreateAnnouncementInput> {
  id: string
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const announcementKeys = {
  all:    ['announcements'] as const,
  active: () => [...announcementKeys.all, 'active'] as const,
  admin:  () => [...announcementKeys.all, 'admin'] as const,
}

// ---------------------------------------------------------------------------
// useActiveAnnouncements — banner de anúncios para usuário logado
// ---------------------------------------------------------------------------

export function useActiveAnnouncements() {
  return useQuery<Announcement[], Error>({
    queryKey: announcementKeys.active(),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_active_announcements')
      if (error) throw new Error(error.message)
      return (data ?? []) as Announcement[]
    },
    staleTime: 60_000,
  })
}

// ---------------------------------------------------------------------------
// useDismissAnnouncement — usuário dispensa um anúncio
// ---------------------------------------------------------------------------

export function useDismissAnnouncement() {
  const qc = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (announcementId) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return // anon: apenas esconde localmente (sem persistência)

      const { error } = await supabase
        .from('announcement_dismissals')
        .insert({ user_id: user.id, announcement_id: announcementId })
      if (error && !error.message.includes('unique')) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: announcementKeys.active() })
    },
  })
}

// ---------------------------------------------------------------------------
// useAdminAnnouncements — lista todos (admin)
// ---------------------------------------------------------------------------

export function useAdminAnnouncements() {
  return useQuery<Announcement[], Error>({
    queryKey: announcementKeys.admin(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw new Error(error.message)
      return (data ?? []) as Announcement[]
    },
  })
}

// ---------------------------------------------------------------------------
// useCreateAnnouncement — admin cria anúncio
// ---------------------------------------------------------------------------

export function useCreateAnnouncement() {
  const qc = useQueryClient()

  return useMutation<void, Error, CreateAnnouncementInput>({
    mutationFn: async (input) => {
      const { error } = await supabase.from('announcements').insert({
        title:           input.title.trim(),
        body:            input.body.trim(),
        target_audience: input.target_audience ?? 'all',
        severity:        input.severity ?? 'info',
        is_active:       input.is_active ?? true,
        starts_at:       input.starts_at ?? new Date().toISOString(),
        ends_at:         input.ends_at ?? null,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      toast.success('Anúncio criado com sucesso!')
      qc.invalidateQueries({ queryKey: announcementKeys.all })
    },
    onError: () => toast.error('Erro ao criar anúncio.'),
  })
}

// ---------------------------------------------------------------------------
// useUpdateAnnouncement — admin edita anúncio
// ---------------------------------------------------------------------------

export function useUpdateAnnouncement() {
  const qc = useQueryClient()

  return useMutation<void, Error, UpdateAnnouncementInput>({
    mutationFn: async ({ id, ...fields }) => {
      const { error } = await supabase
        .from('announcements')
        .update(fields)
        .eq('id', id)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      toast.success('Anúncio atualizado!')
      qc.invalidateQueries({ queryKey: announcementKeys.all })
    },
    onError: () => toast.error('Erro ao atualizar anúncio.'),
  })
}
